import { FC } from "react";
import { isNullOrUndefined } from "@app/shared/utility/isNullOrUndefined";
import { NOT_AVAILABLE_PLACEHOLDER } from "@app/projects/zip-projects/maps/ZipCoverageMapConstants";

interface ZipCoverageMapZipDetailInfoProps {
  zipCode: string;
  zipName: string | null;
  zipClass: string | null;
  primaryCounty: string | null;
  state: string | null;
  specialtyGroupName: string;
  entityType: string | null;
  passPercentage: number | null;
  minimumProviderCount: number | null;
  minimumDistancePhrase: string | null;
}

// Thin copy of county's NetworkCoverageMapCountyDetailInfo, adapted for zip. Heading = zip + primary county +
// state (zip class shows in the badge below); missing values render as "---".
const ZipCoverageMapZipDetailInfo: FC<ZipCoverageMapZipDetailInfoProps> = ({
  zipCode,
  zipName,
  zipClass,
  primaryCounty,
  state,
  specialtyGroupName,
  entityType,
  passPercentage,
  minimumProviderCount,
  minimumDistancePhrase,
}) => {
  const passText = isNullOrUndefined(passPercentage) ? NOT_AVAILABLE_PLACEHOLDER : `${passPercentage}`;
  const providerCountText = isNullOrUndefined(minimumProviderCount)
    ? NOT_AVAILABLE_PLACEHOLDER
    : `${minimumProviderCount}`;
  // Mirrors county's "{count} {specialty} {entityType}"; entityType dropped when absent.
  const specialtyText = [specialtyGroupName, entityType].filter(Boolean).join(" ") || NOT_AVAILABLE_PLACEHOLDER;
  const distanceText = minimumDistancePhrase ?? NOT_AVAILABLE_PLACEHOLDER;
  const zipLabel = zipName ? `${zipCode} - ${zipName}` : zipCode;
  const location = primaryCounty && state ? `${primaryCounty} County, ${state}` : null;

  return (
    <div>
      <h4
        className="header-s qes-zip-coverage-map-details__callout-heading"
        data-testid="qes-zip-coverage-map-details__callout-heading"
      >
        {zipLabel}
      </h4>
      {location && (
        <span
          className="qes-zip-coverage-map-details__callout-subline"
          data-testid="qes-zip-coverage-map-details__callout-subline"
        >
          {location}
        </span>
      )}
      <p
        className="qes-zip-coverage-map-details__callout-requirement"
        data-testid="qes-zip-coverage-map-details__callout-requirement"
      >
        {zipClass ? `${zipClass} zip code` : "Zip Code"} where networks are required to cover <strong>{passText}%</strong> of the beneficiaries with access to
        <strong> {providerCountText} {specialtyText}</strong> within
        <strong> {distanceText}</strong>.
      </p>
    </div>
  );
};

export default ZipCoverageMapZipDetailInfo;
